// Push Notification Service using Firebase Cloud Messaging

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

interface PushNotificationService {
  isSupported: boolean;
  isInitialized: boolean;
  token: string | null;
  initialize: (config: FirebaseConfig, vapidKey: string) => Promise<boolean>;
  requestPermission: () => Promise<NotificationPermission>;
  getToken: () => Promise<string | null>;
  onMessage: (callback: (payload: any) => void) => void;
}

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let currentToken: string | null = null;
let currentVapidKey: string | null = null;
let messageCallback: ((payload: any) => void) | null = null;

// Check if push notifications are supported
const checkSupport = (): boolean => {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

// Initialize Firebase and register service worker
const initialize = async (config: FirebaseConfig, vapidKey: string): Promise<boolean> => {
  if (!checkSupport()) {
    console.warn('Push notifications are not supported in this browser');
    return false;
  }

  try {
    // Store vapidKey for later use
    currentVapidKey = vapidKey;

    // Initialize Firebase
    firebaseApp = initializeApp(config);

    // Use existing service worker registration (from vite-plugin-pwa)
    const registration = await navigator.serviceWorker.ready;

    console.log('Service Worker registered:', registration.scope);

    // Initialize Firebase Messaging
    messaging = getMessaging(firebaseApp);

    // Set up foreground message handler
    onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);

      // Show notification manually for foreground
      if (Notification.permission === 'granted') {
        const title = payload.notification?.title || 'TrackFleet Alert';
        const options: NotificationOptions = {
          body: payload.notification?.body || '',
          icon: '/logo.png',
          badge: '/logo.png',
          tag: payload.data?.alertId || 'trackfleet-notification',
          data: payload.data,
          requireInteraction: true
        };

        new Notification(title, options);
      }

      // Call custom callback if set
      if (messageCallback) {
        messageCallback(payload);
      }
    });

    // Try to get token if permission already granted
    if (Notification.permission === 'granted') {
      currentToken = await getToken(messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: registration
      });
    }

    return true;
  } catch (error) {
    console.error('Failed to initialize push notifications:', error);
    return false;
  }
};

// Request notification permission
const requestPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
};

// Get FCM token
const getFcmToken = async (): Promise<string | null> => {
  if (!messaging) {
    console.warn('Push notifications not initialized');
    return null;
  }

  if (Notification.permission !== 'granted') {
    const permission = await requestPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return null;
    }
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    currentToken = await getToken(messaging, {
      vapidKey: currentVapidKey || undefined,
      serviceWorkerRegistration: registration
    });
    return currentToken;
  } catch (error) {
    console.error('Failed to get FCM token:', error);
    return null;
  }
};

// Set message callback for foreground messages
const setMessageCallback = (callback: (payload: any) => void): void => {
  messageCallback = callback;
};

// Listen for notification clicks from service worker
if (typeof window !== 'undefined') {
  navigator.serviceWorker?.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICKED') {
      console.log('Notification clicked:', event.data.data);
      // Handle notification click in app (e.g., navigate to alerts page)
      if (event.data.data?.alertId) {
        window.location.href = `/alerts?id=${event.data.data.alertId}`;
      }
    }
  });
}

export const pushNotificationService: PushNotificationService = {
  get isSupported() {
    return checkSupport();
  },
  get isInitialized() {
    return !!messaging;
  },
  get token() {
    return currentToken;
  },
  initialize,
  requestPermission,
  getToken: getFcmToken,
  onMessage: setMessageCallback
};

export default pushNotificationService;
